import { atom, getDefaultStore } from "jotai";
import audioContextService from "../Player/waveEdit/AudioContextService";
import { produce } from "immer";
import { AudioSequence } from "../Player/waveEdit/AudioSequence";
import VUCalculator from "./VuMeter/VUCalculator";
import { ErrorService } from "src/components/Error/ErrorService";
import languageService from "src/utils/languageService";
export const MAX_MINUTES_OF_RECORDING = 3;

export const RecorderStates = {
    UNINITIALIZED: 0,
    RECORDING: 1,
    PAUSED: 2,
    FINISHED: 3,
};

export const TestStates = {
    IDLE: 0,
    RECORDING: 1,
    READY: 2,
    PLAYING: 3,
};

export type TestRecordingState = {
    state: number,
};

export type Recordingstate = {
    state: number,
    recordingLength: number,
    gain: number,
    maxLenInSec: number,
    dialogOpen: boolean

}

const store = getDefaultStore();
const recordStateAtom = atom<Recordingstate>({
    dialogOpen: false, state: 0,
    recordingLength: 0, gain: 0,
    maxLenInSec: MAX_MINUTES_OF_RECORDING * 60
});
recordStateAtom.debugLabel = "recordStateAtom";

export const gainAtom = atom<number>(1.5);
gainAtom.debugLabel = "gainAtom";

const SELECTED_MIC_STORAGE_KEY = "ln-selected-mic";
export const selectedMicAtom = atom<string | undefined>(
    localStorage?.getItem(SELECTED_MIC_STORAGE_KEY) ?? undefined
);
selectedMicAtom.debugLabel = "selectedMicAtom";

export const recordingAtom = atom(get => get(recordStateAtom));

const testStateAtom = atom<TestRecordingState>({ state: TestStates.IDLE });
export const testRecordingAtom = atom(get => get(testStateAtom));

export class RecorderService {

    recordingNode?: AudioWorkletNode;
    currentPort?: MessagePort;
    recordedData?: Float32Array[];
    audioContext?: AudioContext;
    mono = true;
    gainInNode?: GainNode;
    vUCalculator?: VUCalculator;
    micSourceNode?: MediaStreamAudioSourceNode;
    analyserNode?: AnalyserNode;
    gainOutNode?: GainNode;

    private isTestRecording = false;
    private testRecordedData?: Float32Array[];
    private testSourceNode?: AudioBufferSourceNode;
    private testPlaybackRafId?: number;
    private discardPendingTestBuffer = false;


    constructor() {

        store.sub(gainAtom, () => {
            if (this.gainInNode) {
                let g = store.get(gainAtom);
                this.gainInNode.gain.value = g;
            }
        });

        store.sub(selectedMicAtom, async () => {
            const val = store.get(selectedMicAtom);
            if (val) {
                localStorage?.setItem(SELECTED_MIC_STORAGE_KEY, val);
            } else {
                localStorage?.removeItem(SELECTED_MIC_STORAGE_KEY);
            }

            const state = store.get(recordStateAtom);
            if (state.dialogOpen && state.state !== RecorderStates.RECORDING) {
                this.Reset();
                await this.initializeAudio(this.mono);
            }
        });
    }

    public get dialogOpen() {
        return store.get(recordStateAtom).dialogOpen;
    }

    public set dialogOpen(value: boolean) {
        store.set(recordStateAtom, produce(store.get(recordStateAtom),
            (draft) => { draft.dialogOpen = value }));
    }

    private setState(state: number) {
        var data = store.get(recordStateAtom);
        if (!data) return;

        const newstate = produce(data, (draft) => {
            draft.state = state;
        })

        store.set(recordStateAtom, newstate);
    }

    private setRecLenAndGain(len: number, gain: number) {
        var data = store.get(recordStateAtom);
        if (!data) return;

        const newstate = produce(data, (draft) => {
            draft.recordingLength = len;
            draft.gain = gain;
        })

        store.set(recordStateAtom, newstate);
    }

    private recordingEventCallback = async (event: MessageEvent<any>) => {
        switch (event.data.message) {

            case 'MAX_RECORDING_LENGTH_REACHED':
                if (this.isTestRecording) {
                    this.testRecordedData = event.data.buffer;
                    this.isTestRecording = false;
                    this.clearRecording();
                    store.set(testStateAtom, { state: TestStates.READY });
                } else {
                    this.setState(RecorderStates.FINISHED);
                    this.recordedData = event.data.buffer;
                }
            // falls through
            case 'UPDATE_RECORDING_LENGTH_AND_VISUALIZERS':
                if (this.audioContext) {
                    const len = event.data.recordingLength / this.audioContext.sampleRate;
                    const vol = this.vUCalculator!.GetVolume();
                    this.setRecLenAndGain(len, 1 - vol);
                }
                break;

            case 'SHARE_RECORDING_BUFFER':
                if (this.isTestRecording) {
                    if (!this.discardPendingTestBuffer) {
                        this.testRecordedData = event.data.buffer;
                        store.set(testStateAtom, { state: TestStates.READY });
                    }

                    this.discardPendingTestBuffer = false;
                    this.isTestRecording = false;
                    this.clearRecording();
                    
                } else {
                    // we can end up here after test recordings only
                    // do not  save data if zero length
                    if( event.data.buffer[0].length > 0)
                        this.recordedData = event.data.buffer;
                    this.setState(RecorderStates.PAUSED);
                }
                break;
        }
    };

    public Reset() {
        this.cleanupTestRecording();
        this.setRecLenAndGain(0, 0);
        this.setState(RecorderStates.UNINITIALIZED);
        this.recordedData = undefined;

        this.gainInNode?.disconnect();
        this.gainInNode = undefined;

        this.recordingNode?.disconnect();
        this.recordingNode = undefined;

        this.micSourceNode?.disconnect();
        this.micSourceNode = undefined;

        this.gainOutNode?.disconnect();
        this.gainOutNode = undefined;

        this.analyserNode?.disconnect();
        this.analyserNode = undefined;

        this.audioContext?.close();
        this.audioContext = undefined;

    }

    public async initializeAudio(asMono: boolean) {

        this.setState(RecorderStates.UNINITIALIZED);
        this.mono = asMono;
        this.audioContext = this.audioContext ?? new AudioContext({
            sampleRate: audioContextService.Context.sampleRate,
        });


        // Get user's microphone and connect it to the this.audioContext.
        const selectedDeviceId = store.get(selectedMicAtom);
        let micStream: MediaStream;
        try {
            micStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: false,
                    autoGainControl: false,
                    noiseSuppression: false,
                    ...(selectedDeviceId ? { deviceId: { exact: selectedDeviceId } } : {})
                }
            });

        } catch (e) {
            if (e instanceof OverconstrainedError) {
                store.set(selectedMicAtom, undefined);
                this.audioContext.close();
                this.audioContext = undefined;
                ErrorService.setError({
                    header: languageService.getText("error"), when: new Date(),
                    message: languageService.getText("mic.not.found")
                });
                return;
            }
            else
                throw e;
        }


        this.micSourceNode = new MediaStreamAudioSourceNode(this.audioContext,
            { mediaStream: micStream });

        this.gainInNode = new GainNode(this.audioContext);
        this.gainOutNode = new GainNode(this.audioContext);
        this.analyserNode = new AnalyserNode(this.audioContext);

        this.vUCalculator = new VUCalculator(-40, this.analyserNode, 32, 6);

        const recordingProperties = {
            numberOfChannels: this.micSourceNode.channelCount,
            sampleRate: this.audioContext.sampleRate,
            maxFrameCount: this.audioContext.sampleRate * MAX_MINUTES_OF_RECORDING * 60
        };


        if (!this.recordingNode) {
            this.recordingNode = await this.setupRecordingWorkletNode(
                recordingProperties, this.audioContext);
        }



        // const waveform = new Waveform('#recording-canvas', analyserNode, 32);
        // const vuMeter = new VUMeter('#vu-meter', -40, analyserNode, 32, 6);

        // We can pass this port across the app and let components handle
        // their relevant messages.
        //const visualizerCallback = setupVisualizers(waveform, vuMeter);

        this.recordingNode.port.onmessage = this.recordingEventCallback;
        this.clearRecording();

        this.gainInNode.gain.value = 1.5;
        this.gainOutNode.gain.value = 0;

        this.micSourceNode
            .connect(this.gainInNode)
            .connect(this.analyserNode)
            .connect(this.recordingNode)
            .connect(this.gainOutNode)
            .connect(this.audioContext.destination);
    }

    public haveRecording() {
        return !!this.recordedData;
    }
    
    public clearRecording() {
        if (this.recordingNode) {
            this.recordingNode.port.postMessage({
                message: 'CLEAR_RECORDING_STATE',
            });
        }
    }

    public startTestRecord() {
        if (!this.recordingNode) return;
        this.isTestRecording = true;
        this.testRecordedData = undefined;
        store.set(testStateAtom, { state: TestStates.RECORDING });
        this.recordingNode.port.postMessage({
            message: 'UPDATE_RECORDING_STATE',
            setRecording: true,
            mono: this.mono
        });
    }

    public stopTestRecord() {
        if (!this.recordingNode) return;
        this.recordingNode.port.postMessage({
            message: 'UPDATE_RECORDING_STATE',
            setRecording: false,
            mono: this.mono
        });
    }

    public playTestRecording() {
        if (!this.testRecordedData || !this.audioContext || !this.analyserNode) return;
        const numSamples = this.testRecordedData[0]?.length ?? 0;
        if (numSamples === 0) return;

        if (this.gainInNode) this.gainInNode.gain.value = 0;

        const buffer = this.audioContext.createBuffer(
            this.testRecordedData.length, numSamples, this.audioContext.sampleRate);
        for (let i = 0; i < this.testRecordedData.length; i++) {
            buffer.copyToChannel(this.testRecordedData[i] as Float32Array<ArrayBuffer>, i);
        }

        this.testSourceNode = this.audioContext.createBufferSource();
        this.testSourceNode.buffer = buffer;
        this.testSourceNode.connect(this.analyserNode);
        this.testSourceNode.connect(this.audioContext.destination);
        this.testSourceNode.onended = () => this._finishTestPlayback();

        store.set(testStateAtom, { state: TestStates.PLAYING });
        this.startVuLoop();
        this.testSourceNode.start();
    }

    public stopTestPlayback() {
        if (this.testSourceNode) {
            try { this.testSourceNode.stop(); } catch { /* already ended */ }
        }
        this._finishTestPlayback();
    }

    public cleanupTestRecording() {
        if (this.isTestRecording) {
            this.discardPendingTestBuffer = true;
            this.recordingNode?.port.postMessage({
                message: 'UPDATE_RECORDING_STATE',
                setRecording: false,
                mono: this.mono
            });
        }
        this.stopTestPlayback();
        this.testRecordedData = undefined;
        store.set(testStateAtom, { state: TestStates.IDLE });
    }

    private _finishTestPlayback() {
        if (!this.testSourceNode) return;
        this.testSourceNode.disconnect();
        this.testSourceNode = undefined;
        this.stopVuLoop();
        if (this.gainInNode) this.gainInNode.gain.value = store.get(gainAtom);
        if (store.get(testStateAtom).state === TestStates.PLAYING) {
            store.set(testStateAtom, { state: TestStates.READY });
        }
    }

    private startVuLoop() {
        if (this.testPlaybackRafId !== undefined) return;
        const loop = () => {
            if (this.vUCalculator) {
                const vol = this.vUCalculator.GetVolume();
                const data = store.get(recordStateAtom);
                store.set(recordStateAtom, produce(data, draft => { draft.gain = 1 - vol; }));
            }
            this.testPlaybackRafId = requestAnimationFrame(loop);
        };
        this.testPlaybackRafId = requestAnimationFrame(loop);
    }

    private stopVuLoop() {
        if (this.testPlaybackRafId !== undefined) {
            cancelAnimationFrame(this.testPlaybackRafId);
            this.testPlaybackRafId = undefined;
        }
    }

    public getDataAsAudioSequences() {
        if (!this.recordedData) {
            return undefined;
        }
        const rate = audioContextService.Context.sampleRate;
        return this.recordedData.map(r => new AudioSequence(rate, r));
    }

    /**
 * Create and set up a WorkletNode to record audio from a microphone.
 * @param {object} recordingProperties
 * @return {AudioWorkletNode} Recording node related components for
 * the app.
 */
    private async setupRecordingWorkletNode(recordingProperties: any, audioContext: AudioContext) {
        try {
            await audioContext.audioWorklet.addModule('/workers/recording-processor.js');
        }
        catch (e) {
            console.error(e);
        }
        const workletRecordingNode = new AudioWorkletNode(
            audioContext,
            'recording-processor',
            {
                processorOptions: recordingProperties,
            },
        );

        return workletRecordingNode;
    }

    public startRecord = () => {
        if (!this.recordingNode) return;
        this.recordedData = undefined;
        this.setState(RecorderStates.RECORDING);
        this.recordingNode.port.postMessage({
            message: 'UPDATE_RECORDING_STATE',
            setRecording: true,
            mono: this.mono
        });
    }

    public stopRecord = () => {
        if (!this.recordingNode) return;
        this.setState(RecorderStates.PAUSED);
        this.recordingNode.port.postMessage({
            message: 'UPDATE_RECORDING_STATE',
            setRecording: false,
            mono: this.mono
        });


    }

}

const recorderService = new RecorderService();
export default recorderService;
